/* eslint-disable complexity */
import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import Truncate from 'react-truncate';
import ProviderModal from '../providerModals/ProviderDetialsModal';
import ProviderInteractionsModal from '../providerModals/ProviderInteractionsModal';
import {
  DataVendor,
  ExternalLink,
  ProposalEntitySubmittedBy,
  RootState,
  SearchEntity,
} from 'v2/models';
import { useSelector } from 'react-redux';
import { PERMISSION_NAME, hasPermission } from 'v2/utils/permissions';
import { useGetActivities } from 'v2/hooks/useGetActivities';
import AvatarNameDetail from '../AvatarNameDetail';
import { useTranslation } from 'react-i18next';
import { PostProcessKeys, TranslationKeys } from 'translations';

type ProviderInformationCardProps = {
  submittedByTitle: string;
  submittedBy?: ProposalEntitySubmittedBy;
  dataProvider: {
    id?: number;
    name: string;
    vague_description: string;
    short_description: string;
    long_description: string;
    logo: string;
    external_links?: ExternalLink[];
  };
  hideIdentity: boolean;
  dataVendorQuery: { data: DataVendor | undefined; isSuccess: boolean };
  request: SearchEntity;
};

const ProviderInformationCard = ({
  submittedByTitle,
  submittedBy,
  dataProvider,
  hideIdentity,
  dataVendorQuery,
  request,
}: ProviderInformationCardProps) => {
  const { t } = useTranslation();
  const permissions = useSelector(
    (state: RootState) => state.permissions.permissions
  );
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [showInteractionsModal, setShowInteractionsModal] = useState(false);

  const dataVendorData = dataVendorQuery.data;
  const { data: activityData } = useGetActivities({
    relatedTable: 'DataVendor',
    relatedObjectId: dataVendorData && dataVendorData.id,
    filterSelectedOption: '',
    system_generated: false,
    ordering: '-timestamp',
    enabled:
      hasPermission(permissions, [PERMISSION_NAME.DRM]) &&
      !!dataVendorQuery.data,
  });
  const activitiesCount = (activityData && activityData.pages[0]?.count) || 0;

  const showInteractionsButton =
    dataVendorQuery.isSuccess &&
    activityData &&
    dataVendorData &&
    activitiesCount > 0;
  const showFullProfileButton =
    (!hasPermission(permissions, [PERMISSION_NAME.DRM]) && !hideIdentity) ||
    (!hideIdentity && dataVendorQuery.isSuccess && !dataVendorData);

  const getDescription = () => {
    if (hideIdentity) {
      return dataProvider.vague_description;
    } else {
      return dataProvider.short_description;
    }
  };

  return (
    <div className="bg-light p-4 border border-light5 border-left-0 rounded-right">
      <AvatarNameDetail
        subtitle={submittedByTitle}
        image={dataProvider.logo}
        title={
          dataProvider.name ||
          t(TranslationKeys.dataProvider_one, {
            postProcess: PostProcessKeys.TitleCase,
          })
        }
        newTab={
          dataVendorData && `/data-provider-details/${dataVendorData.id}/detail`
        }
      />
      <p className="text-light4 pt-3">
        <Truncate lines={4}>
          {getDescription() || 'No Description Available'}
        </Truncate>
      </p>
      {showFullProfileButton ? (
        <>
          <Button
            className="p-0"
            variant="link"
            onClick={() => setShowProviderModal(true)}
          >
            Full Profile
          </Button>
          <ProviderModal
            show={showProviderModal}
            onHide={() => setShowProviderModal(false)}
            providerLogo={dataProvider.logo}
            providerName={dataProvider.name}
            submittedBy={submittedByTitle}
            description={dataProvider.long_description}
            providerExternalLinks={dataProvider.external_links}
          />
        </>
      ) : null}
      <>
        {showInteractionsButton && (
          <>
            <Button
              className="p-0"
              variant="link"
              onClick={() => setShowInteractionsModal(true)}
            >
              {`Prior Interactions (${activitiesCount})`}
            </Button>
            <ProviderInteractionsModal
              show={showInteractionsModal}
              onHide={() => setShowInteractionsModal(false)}
              request={request}
              hideIdentity={hideIdentity}
              dataVendor={dataVendorData}
              submittedBy={submittedBy}
            />
          </>
        )}
      </>
    </div>
  );
};

export default ProviderInformationCard;
